<mat-toolbar color="primary" class="action-toolbar">
    <button mat-icon-button (click)="returnToParent()" matTooltip="Back">
        <mat-icon color="primary">arrow_back_ios</mat-icon>
    </button>
    <h3>{{ patient | patientHeading:(isEditing ? 'Edit Lab Order' : 'New Lab Order') }}</h3>
    <div>
        <button mat-icon-button *ngIf="canDelete" (click)="deleteLabOrder()" matTooltip="Delete Lab Order">
            <mat-icon color="warn">delete</mat-icon>
        </button>
        <button mat-icon-button [disabled]="form.invalid || !canSubmitLabOrder" (click)="saveLabOrder()"
            matTooltip="Save Lab Order">
            <mat-icon [color]="form.invalid || !canSubmitLabOrder ? '' : 'primary'">save</mat-icon>
        </button>
    </div>
</mat-toolbar>

<div class="lab-order-details-container">
    <form [formGroup]="form">
        <!-- Physician Selection (only for non-doctor users) - Show above warning banner -->
        <mat-card *ngIf="isNonDoctorUser && groupUsers.length > 0" class="provider-selection-card">
            <mat-card-content>
                <div class="form-row">
                    <mat-form-field appearance="outline">
                        <mat-label>Requesting Physician</mat-label>
                        <mat-select formControlName="physicianId"
                            (selectionChange)="onPhysicianSelectionChanged($event.value)">
                            <mat-option *ngFor="let user of groupUsers" [value]="user.userId">
                                {{user.title}} {{user.name}} {{user.surname}}<span *ngIf="user.lancetCode"> ✓</span>
                            </mat-option>
                        </mat-select>
                    </mat-form-field>
                </div>
            </mat-card-content>
        </mat-card>

        <!-- Warning banner if lancetCode is missing -->
        <!-- Debug: canSubmitLabOrder = {{ canSubmitLabOrder }}, currentUser = {{ currentUser | json }}, currentGroup = {{ currentGroup | json }} -->
        <mat-card *ngIf="!canSubmitLabOrder" class="warning-banner-card">
            <mat-card-content>
                <div class="warning-banner">
                    <mat-icon>warning</mat-icon>
                    <div class="warning-content">
                        <strong>Lab Order Submission Disabled</strong>
                        <p *ngIf="!isNonDoctorUser">Lab orders require a Lancet Code for your user profile.</p>
                        <p *ngIf="isNonDoctorUser && selectedPhysician">
                            Lab orders require a Lancet Code for <strong>{{ selectedPhysician.title }} {{
                                selectedPhysician.name }} {{ selectedPhysician.surname }}</strong>
                        </p>
                        <div>
                            <p *ngIf="!isNonDoctorUser">Please configure your user Lancet Code.</p>
                            <p *ngIf="isNonDoctorUser && selectedPhysician">Please configure the Lancet Code for the
                                selected physician.</p>
                            <p><a href="#" (click)="openLabOrderConfigDialog($event)">Click here to configure now</a>
                            </p>
                        </div>
                    </div>
                </div>
            </mat-card-content>
        </mat-card>

        <mat-card *ngIf="canSubmitLabOrder">

            <!-- Form fields - only show if physician is selected (or user is a doctor) -->
            <div *ngIf="hasSelectedPhysician">
                <div class="form-row">
                    <mat-form-field appearance="outline">
                        <mat-label>Order Date</mat-label>
                        <input matInput [matDatepicker]="orderDatePicker" formControlName="orderDate"
                            placeholder="DD/MM/YYYY" [max]="todayDate">
                        <mat-datepicker-toggle matSuffix [for]="orderDatePicker"></mat-datepicker-toggle>
                        <mat-datepicker #orderDatePicker></mat-datepicker>
                    </mat-form-field>

                    <mat-form-field appearance="outline">
                        <mat-label>Priority</mat-label>
                        <mat-select formControlName="priority">
                            <mat-option *ngFor="let type of labOrderTypes" [value]="type.id">
                                {{ type.label }}
                            </mat-option>
                        </mat-select>
                    </mat-form-field>

                    <mat-form-field appearance="outline" *ngIf="editingLabOrder?.labResults">
                        <mat-label>Review Status</mat-label>
                        <mat-select formControlName="reviewStatus">
                            <mat-option *ngFor="let status of reviewStatuses" [value]="status.id">
                                {{ status.label }}
                            </mat-option>
                        </mat-select>
                    </mat-form-field>
                </div>

                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Notes</mat-label>
                    <textarea matInput formControlName="notes" rows="3"
                        placeholder="Additional notes for the lab order..."></textarea>
                </mat-form-field>

                <!-- Lab Test Search Section - Always Visible -->
                <div class="lab-test-search-section">
                    <h4>Select Lab Tests</h4>

                    <!-- Select Bundles -->
                    <div class="bundles-subsection" *ngIf="labTestBundles.length > 0">
                        <!--  <h5>Select Bundles</h5> -->
                        <mat-form-field appearance="outline" class="full-width m-b-0">
                            <mat-label>Select Bundles</mat-label>
                            <mat-select multiple formControlName="selectedBundles"
                                (selectionChange)="onBundleSelectionChanged($event.value)">
                                <mat-option *ngFor="let bundle of labTestBundles" [value]="bundle">
                                    {{ bundle.label }} ({{ bundle.lab || 'Lancet' }})
                                </mat-option>
                            </mat-select>
                        </mat-form-field>
                    </div>

                    <!-- No Bundles Available Message -->
                    <div class="no-bundles-message" *ngIf="labTestBundles.length === 0">
                        <mat-card>
                            <mat-card-content>
                                <p>No lab test bundles are available. Please configure your preferred lab test bundles
                                    in
                                    your profile settings.</p>
                            </mat-card-content>
                        </mat-card>
                    </div>

                    <!-- Search Lab Tests -->
                    <div class="search-subsection">
                        <!-- <h5>Search Lab Tests</h5> -->
                        <app-lab-test-selector [patient]="patient" [checklistItems]="selectedLabTests"
                            [mode]="'checklist'" [showChecklist]="true" [showCategoryFilter]="false"
                            [showLabProvider]="false" [disableAdding]="false" [pluralTitle]="'Lab Tests'"
                            [singularTitle]="'Lab Test'" (checkedItemsChanged)="onLabTestsChanged($event)"
                            (checkedTestsChanged)="onLabTestsSelectionChanged($event)">
                        </app-lab-test-selector>
                    </div>
                </div>

                <!-- Other Available Tests Section -->
                <div class="other-tests-section"
                    *ngIf="showIndividualTests && selectedLabTests.length > 0 && allOtherTests.length > 0">
                    <h4>Other Favourite Tests</h4>
                    <app-lab-test-selector [patient]="patient" [checklistItems]="allOtherTests" [mode]="'checklist'"
                        [showChecklist]="true" [showSearch]="false" [showCategoryFilter]="false"
                        [showLabProvider]="false" [disableAdding]="false" [pluralTitle]="'Lab Tests'"
                        [singularTitle]="'Lab Test'" (checkedItemsChanged)="onOtherTestsChanged($event)"
                        (checkedTestsChanged)="onOtherTestsSelectionChanged($event)">
                    </app-lab-test-selector>
                </div>

                <!-- Lab Results Section (only show if results exist) -->
                <div class="lab-results-section" *ngIf="editingLabOrder?.labResults">
                    <h4>Lab Results</h4>
                    <mat-form-field appearance="outline" class="full-width">
                        <mat-label>Result Summary</mat-label>
                        <textarea matInput formControlName="resultSummary" rows="4"
                            placeholder="Summary of lab results..."></textarea>
                    </mat-form-field>
                </div>
            </div>
            <!-- End of hasSelectedPhysician wrapper -->
        </mat-card>
    </form>
</div>